import * as React from "react";
import {
  createContext,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import type { HotelResult } from "@agent/pages/new-booking/SearchResults";
import * as moment from "moment";
import { SEARCH_TYPES } from "@config/const/enums";
import { useService } from "@infrastructure/api/hooks/use-service";
import { Requests } from "@infrastructure/api/requests";

export type WishListItem = {
  date: string;
  hash: string;
  searchType: string;
  adl: number;
  chd: number;
  chd_ages: number[];
  hotel_id: number;
  name: string;
  image: string;
  category: number;
  location: string;
  room: string;
  room_type: string;
  board: string;
  checkin: string;
  checkout: string;
  price: number;
  departure: string | null;
  return: string | null;
};

export type ContextType = {
  loading: boolean;
  wishList: WishListItem[];
  isInWishList(result: HotelResult): boolean;
  addToWishList(result: HotelResult): void;
  removeFromWishList(result: string | HotelResult): void;
  sendByEmail(email: string, items: WishListItem[], remove?: boolean): void;
};

const WishListContext = createContext<ContextType>(null);

export const useWishList = () => {
  const context = useContext(WishListContext);
  if (!context) {
    console.error("useWishList must be used within the WishListContext");
  }
  return context;
};

export function WishListProvider({ children }): ReactElement {
  const value = useProvideWishList();
  return (
    <WishListContext.Provider value={value}>
      {children}
    </WishListContext.Provider>
  );
}

const getSearchType = (result: HotelResult) => {
  return result.depart_flight ? SEARCH_TYPES.charter : SEARCH_TYPES.hotel;
};

const getCompareHash = (item: Partial<WishListItem>): string => {
  return [
    item.searchType,
    item.adl,
    item.chd,
    ...item.chd_ages,
    item.hotel_id,
    item.room,
    item.room_type,
    item.board,
    item.price,
    item.checkin,
    item.checkout,
    item.departure,
    item.return,
  ].join(",");
};

const getWishListItem = (result: HotelResult): WishListItem => {
  const item = {
    date: moment.utc().format(),
    searchType: getSearchType(result),
    adl: result.adl,
    chd: result.chd,
    chd_ages: result.chd_ages,
    hotel_id: result.id,
    image: result.image.medium,
    name: result.name,
    category: result.category,
    location: result.location,
    room: result.room,
    room_type: result.room_type,
    board: result.board,
    checkin: result.checkin,
    checkout: result.checkout,
    departure: result.depart_flight?.flight_date,
    return: result.return_flight?.flight_date,
    price: result.price,
  };
  return { ...item, hash: getCompareHash(item) };
};

// const getJson = (item: WishListItem): string => {
//   return JSON.stringify(item, Object.keys(item).sort(), 0);
// };

function useProvideWishList() {
  const [wishList, setWishList] = useState<WishListItem[]>(() => {
    const items: WishListItem[] = JSON.parse(
      localStorage.getItem("wishlist") ?? "[]"
    );
    const now = moment.utc();
    return items.filter(
      (item) => moment.utc(item.date).diff(now, "hours") < 24
    );
  });

  useEffect(() => {
    localStorage.setItem("wishlist", JSON.stringify(wishList));
  }, [wishList]);

  const { loading, requestAsync: requestSendByEmail } = useService(
    Requests.wishlist
  );

  const addToWishList = useCallback((result: HotelResult) => {
    const item = getWishListItem(result);
    setWishList((current) => [...current, item]);
  }, []);

  const removeFromWishList = useCallback((result: string | HotelResult) => {
    let hash: string = null;
    if (typeof result === "string") {
      hash = result;
    } else {
      hash = getWishListItem(result).hash;
    }
    setWishList((current) => current.filter((it) => hash !== it.hash));
  }, []);

  const isInWishList = (result: HotelResult): boolean => {
    const item = getWishListItem(result);
    return wishList.findIndex((it) => item.hash === it.hash) >= 0;
  };

  const sendByEmail = async (
    email: string,
    items: WishListItem[],
    remove = true
  ) => {
    try {
      const result = await requestSendByEmail({
        payload: {
          email,
          wishlist: items,
        },
      });
      if (result && remove) {
        setWishList((current) =>
          current.filter((item) => !items.find((it) => it.hash === item.hash))
        );
      }
      return true;
    } catch (error) {
      return false;
    }
  };

  return {
    loading,
    wishList,
    isInWishList,
    addToWishList,
    removeFromWishList,
    sendByEmail,
  };
}
